// Per-field language maps for website-builder content.
//
// Each user-facing text prop on a Craft.js node is either a bare string
// (language-neutral — shown for every language) or a Record<locale, string>
// (localized, keyed by full BCP-47 locale). This keeps untouched default-prop
// literals valid while letting any field be translated on first edit.
//
// Mirror of website-builder-frontend/src/runtime/lib/resolve-text.ts — kept
// byte-identical by hand (no shared package between the editor and the runtime
// twin). Build on resolve-lang.ts's tolerant resolver so "de" and "de-DE"
// interoperate the same way email templates resolve.

import { resolveLangValue } from "./resolve-lang";

/** A localizable text prop: a neutral string or a per-locale map. */
export type LangText = string | Record<string, string> | undefined | null;

/**
 * Resolve a text prop to a display string for the given language.
 *   string    → the string itself (language-neutral)
 *   map       → resolveLangValue (lang → primary → any → "")
 *   undefined → ""
 */
export function resolveText(
  value: LangText,
  lang: string | null | undefined,
  primary: string | null | undefined,
): string {
  if (value === undefined || value === null) return "";
  if (typeof value === "string") return value;
  return resolveLangValue(value, lang, primary, "");
}

/**
 * Pure variant: return the new LangText value for `lang`, upgrading a bare
 * string / undefined into a per-locale map (seeding the old string under
 * `primary`). Use for array elements (e.g. stepLabels) where there's no single
 * props key to mutate.
 */
export function setLangValue(
  current: LangText,
  lang: string,
  primary: string | null | undefined,
  content: string,
): Record<string, string> {
  if (current !== null && current !== undefined && typeof current === "object") {
    return { ...current, [lang]: content };
  }
  const map: Record<string, string> = {};
  if (typeof current === "string" && current.length > 0 && primary) {
    map[primary] = current;
  }
  map[lang] = content;
  return map;
}

/**
 * Write `content` for `lang` into a Craft.js props draft, in place.
 * Upgrades a bare string / undefined prop into a per-locale map, seeding the
 * existing string under `primary` so the original copy survives as the primary
 * translation. Designed to run inside a Craft `setProp` callback.
 */
export function setLangText(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>,
  key: string,
  lang: string,
  primary: string | null | undefined,
  content: string,
): void {
  const current = props[key];

  // Fallback-preservation: a non-primary language only stores a value that
  // DIFFERS from what the primary language resolves to. If it matches, keep the
  // language key ABSENT so the field stays a live fallback to primary (editing
  // primary later updates it) rather than a frozen copy.
  if (lang !== primary && typeof primary === "string" && primary.length > 0) {
    const primaryValue = resolveText(current, primary, primary);
    if (content === primaryValue) {
      if (
        current !== null &&
        current !== undefined &&
        typeof current === "object" &&
        lang in current
      ) {
        delete current[lang];
      }
      return;
    }
  }

  if (current !== null && current !== undefined && typeof current === "object") {
    current[lang] = content;
    return;
  }

  const map: Record<string, string> = {};
  if (typeof current === "string" && current.length > 0 && primary) {
    map[primary] = current;
  }
  map[lang] = content;
  props[key] = map;
}

/**
 * Remove a single language key from a per-field map, in place. Used when an
 * operator removes a language from the site so orphaned translations don't
 * linger. No-op for bare-string / undefined props.
 */
export function pruneLang(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  props: Record<string, any>,
  key: string,
  lang: string,
): void {
  const current = props[key];
  if (current !== null && typeof current === "object" && lang in current) {
    delete current[lang];
  }
}
